import React, { useState, useEffect, useRef } from 'react';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatDate, formatConsumption } from '../utils/format';

const DownWaterMetersPage = () => {
  const [waterMeters, setWaterMeters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGE_SIZE = 20;

  // AbortController refs
  const totalCountAbortControllerRef = useRef();
  const downAbortControllerRef = useRef();
  const loadMoreAbortControllerRef = useRef();

  // Fetch total count of down water meters
  const fetchTotalCount = async () => {
    const controller = new AbortController();
    totalCountAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const res = await api.get('/api/proxy/wmeters/down/count', { signal });
      setTotalCount(res.data.count);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      }
    }
  };

  // Fetch initial or refreshed list of down water meters
  const fetchDownWaterMeters = async date => {
    setLoading(true);
    const controller = new AbortController();
    downAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const params = { max: PAGE_SIZE };
      if (date) params.start = date.toISOString();
      const res = await api.get('/api/proxy/wmeters/down', { params, signal });
      const items = res.data;
      items.sort((a, b) => new Date(b.lastseen) - new Date(a.lastseen));
      setWaterMeters(items);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      } else {
        // treat cancellation as empty
        setWaterMeters([]);
      }
    }
    setLoading(false);
  };

  // Load more older items without resetting list
  const loadMore = async () => {
    if (!waterMeters.length) return;
    setLoadingMore(true);
    const controller = new AbortController();
    loadMoreAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const oldestMs = Math.min(
        ...waterMeters.map(m => new Date(m.registeredAt).getTime())
      );
      const oldestDate = new Date(oldestMs);
      const params = {
        max: PAGE_SIZE,
        start: oldestDate.toISOString(),
        reverse: true
      };
      const res = await api.get('/api/proxy/wmeters/down', { params, signal });
      let newItems = res.data.filter(m => {
        const id = m._id.$oid || m._id;
        return !waterMeters.some(existing => (existing._id.$oid || existing._id) === id);
      });
      newItems.sort((a, b) => new Date(b.lastseen) - new Date(a.lastseen));
      setWaterMeters(prev => [...prev, ...newItems]);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      }
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchTotalCount();
    fetchDownWaterMeters(startDate);

    return () => {
      totalCountAbortControllerRef.current?.abort();
      downAbortControllerRef.current?.abort();
      loadMoreAbortControllerRef.current?.abort();
    };
  }, [startDate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={isSmall ? 1 : 2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: isSmall ? 1 : 2
        }}
      >
        <Typography variant="h5">
          Down Water Meters ({totalCount})
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start date"
            value={startDate}
            onChange={newDate => setStartDate(newDate)}
            renderInput={params => (
              <TextField {...params} size="small" sx={{ minWidth: 140 }} />
            )}
          />
        </LocalizationProvider>
      </Box>

      {waterMeters.length === 0 ? (
        <Box align="center">
          <PowerOffIcon sx={{ fontSize: isSmall ? 60 : 80, color: theme.palette.text.disabled }} />
          <Typography
            variant={isSmall ? 'h6' : 'h5'}
            color="textSecondary"
            mt={2}
          >
            No down meter found
          </Typography>
          <Typography
            variant={isSmall ? 'body2' : 'subtitle1'}
            color="textSecondary"
            mt={1}
          >
            This page displays devices explicitly switched off by shutdown request.
          </Typography>
        </Box>
      ) : (
        waterMeters.map(device => {
          const id = device._id.$oid || device._id;
          const statusColor = device.criticalState ? 'red' : 'gray';

          if (isSmall) {
            return (
              <Paper
                key={id}
                sx={{ mb: 1, p: 1, cursor: 'pointer' }}
                onClick={() => navigate(`/water-meters/${id}`)}
              >
                <Grid container direction="column" alignItems="center" spacing={1}>
                  <Grid item>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Typography variant="body1" align="center">
                          {device.deviceName}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <FiberManualRecordIcon sx={{ color: statusColor }} fontSize="small" />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Typography variant="caption" color="textSecondary" align="center">
                      Water Meter
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" align="center">
                      BLE Address: {device.bleAddress}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="body2">
                    Last Seen: {device.lastseen ? formatDate(device.lastseen, true) : 'Never'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Consumption: {formatConsumption(device.totalConsumption)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Critical: {device.criticalState ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              </Paper>
            );
          }

          return (
            <Paper
              key={id}
              sx={{ mb: 2, p: 2, cursor: 'pointer' }}
              onClick={() => navigate(`/water-meters/${id}`)}
            >
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid item>
                  <FiberManualRecordIcon sx={{ color: statusColor }} fontSize="small" />
                </Grid>
                <Grid item xs>
                  <Typography variant="subtitle1">{device.deviceName}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Water Meter
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2">
                    BLE Address: {device.bleAddress}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    Last Seen: {device.lastseen ? formatDate(device.lastseen, true) : 'Never'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    Consumption: {formatConsumption(device.totalConsumption)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    Critical: {device.criticalState ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          );
        })
      )}

      {waterMeters.length > 0 && (
        <>
          <Box textAlign="center" mt={2}>
            <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load More'}
            </Button>
          </Box>
          <Box textAlign="right" mt={1}>
            {waterMeters.length} of {totalCount}
          </Box>
        </>
      )}
    </Box>
  );
};

export default DownWaterMetersPage;
